import { Controller } from "@hotwired/stimulus"

// Saves the block it wraps the moment there is something worth keeping, so the
// block needs no Save button: the picture when it is chosen, the line when the
// creator leaves it. Wire the moments per field, not here — the file input asks
// for `change->autosave#save`, the textarea for `blur->autosave#save` — because
// what counts as "done editing" is a property of the field, not of the form.
//
// The write answers in turbo_streams (see CreatorPagesController#update), so
// only the preview and the thumbnail move; this form, and the caret in it, is
// left alone. That is the whole reason the response is not a redirect.
//
// With this controller dead or the bundle unloaded the form still works — it is
// an ordinary form, it has just lost the thing that submits it. That is a real
// regression rather than a graceful one, which is why the handle, the field
// that matters most, kept a button of its own.
export default class extends Controller {
  static targets = ["status"]
  static values = { clearAfter: { type: Number, default: 1500 } }

  connect() {
    // What each text field held when the screen loaded, so a blur that changed
    // nothing costs nothing. Tabbing through a form is the common case and it
    // must not be one request per field.
    this.saved = new Map()
    this.textFields.forEach((el) => this.saved.set(el, el.value))
  }

  disconnect() { clearTimeout(this.timer) }

  save(event) {
    const field = event.target
    if (this.saved.has(field)) {
      if (this.saved.get(field) === field.value) return
      this.saved.set(field, field.value)
    }
    this.element.requestSubmit()
  }

  // Writes the line into the phone beside the form as it is typed, before the
  // blur saves it (Tamara, 20 Oct 2026: the preview only caught up on a click).
  // The frame is the creator's page on the same origin, which keeps an empty
  // #creator_bio in preview mode for this. Save's stream later swaps the whole
  // frame for the saved version, which reads the same.
  mirror(event) {
    const doc = document.querySelector("#page_preview iframe")?.contentDocument
    const bio = doc?.getElementById("creator_bio")
    if (!bio) return
    bio.textContent = event.target.value
    bio.classList.toggle("hidden", event.target.value.trim() === "")
  }

  // On the form's own turbo:submit-end. A refusal already wrote its message
  // into the status line through a stream, so saying "Saved" over it would be
  // both wrong and the last word.
  submitted(event) {
    if (!event.detail.success) return
    this.flash("Saved")
  }

  flash(text) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
    clearTimeout(this.timer)
    this.timer = setTimeout(() => { this.statusTarget.textContent = "" }, this.clearAfterValue)
  }

  get textFields() {
    return this.element.querySelectorAll("textarea, input[type=text]")
  }
}
